import { useState, useEffect, useRef, useId } from "react";
import { fieldType } from "../../../../types/form";
import { DocumentIcon } from "@heroicons/react/24/solid";
import { FileIcon, DownloadIcon } from "../../../ui-icons/upload";
import Trash from "../../../ui-icons/buttons/Trash";
import { useAppSelector } from "src/hooks/redux";
import { selectGetStatus, selectUpdateStatus } from "src/modules/spItems/selectors";
import Loading from "src/components/Loading";
import { selectCurrentUser } from "src/modules/users/selector";
import { addAnalysis } from "src/modules/acadia/constants";
import { documentByCardFilesLibName, siteUrl } from "src/api/config";
import { siteUrlOrSiteApi } from "src/api/config/systemSwitch";
import { FlexCol } from "src/utils/flexLayouts";
import EyeIcon from "src/components/ui-icons/reports/EyeIcon";
import { normalizeJpeg } from "src/pages/DashboardPage/mytasks/utils/utils";
import { isPWA } from "src/utils/mobile";
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { useFilePreview, isPreviewable } from "src/hooks/useFilePreview";
import { FileType } from "src/types/main";
import { downloadForApp } from "src/utils/downloadForApp";


const siteUrlProd = 'https://supply.napitkivmeste.ru'; // для корректного отображения превью в карточке документа

const MAX_FILE_SIZE_DEFAULT = 52428800;
const MAX_FILE_SIZE_VIDEO = 209715200;
const VIDEO_EXT_RE = /\.(mp4|avi|mpeg)$/i;
const DOC_EXT_RE = /\.(docx|xlsx)$/i;

type Props = {
  dataField: fieldType;
  onChange: (data: any) => void;
  errors?: any;
  value: any;
  type: string;
  setIdRemoveElem: any;
  documentID?: any;
  documentCode?: any;
  listUploadFiles?: any;
  openRemoveFileCommentModal?: any;
  setOpenRemoveFileCommentModal?: any;
};

type DownloadProgressState = {
  progress: number;
  loadedBytes: number;
  totalBytes: number | null;
  isLoading: boolean;
};

// UI constraint: file names must never be able to stretch popups/modals beyond the viewport width (PWA/mobile).
// Tech requirement: show at most 25 characters of the file name in UI (with ellipsis); keep full name in title tooltip.
const FILE_NAME_UI_MAX_CHARS = 25;
const formatFileNameForUi = (rawName: unknown, maxChars = FILE_NAME_UI_MAX_CHARS) => {
  const name = typeof rawName === "string" ? rawName : String(rawName ?? "");
  if (!name) return "";
  return name.length > maxChars ? `${name.slice(0, maxChars)}…` : name;
};

const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 Б";
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} ГБ`;
};

const getMobileVideoUrl = async (file: any): Promise<string> => {
  const url = file?.url || file?.FileRef;

  if (!url) {
    return "";
  }

  try {
    const result = await SecureStoragePlugin.get({
      key: "accessToken",
    });

    const token = result.value;

    if (!token) {
      console.error("Access token not found");
      return "";
    }

    const videoPath = new URL(url).pathname;

    console.log("VIDEO PATH:", videoPath);

    const response = await fetch(`${siteUrl}/mobile/video-url`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        path: videoPath,
      }),
    });

    const responseText = await response.text();

    console.log("VIDEO URL STATUS:", response.status);
    console.log("VIDEO URL RESPONSE:", responseText);

    if (!response.ok) {
      throw new Error(
        `video-url failed: ${response.status} ${responseText}`
      );
    }

    const data = JSON.parse(responseText);

    if (!data?.url) {
      return "";
    }

    // Backend возвращает /mobile/video?token=...
    const playableUrl = new URL(data.url, siteUrl).toString();

    console.log("FINAL VIDEO URL:", playableUrl);

    return playableUrl;
  } catch (error) {
    console.error("GET MOBILE VIDEO URL ERROR:", error);
    return "";
  }
};

function MobileVideo({ file }: { file: any }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let cancelled = false;

    const loadVideo = async () => {
      const url = await getMobileVideoUrl(file);

      console.log("FINAL VIDEO URL:", url);

      if (!cancelled && videoRef.current && url) {
        videoRef.current.src = url;
        videoRef.current.load();
      }
    };

    loadVideo();

    return () => {
      cancelled = true;
    };
  }, [file]);

  return (
    <video
      ref={videoRef}
      className="w-full rounded-lg bg-black"
      controls
      playsInline
      preload="metadata"
    />
  );
}

function UploadFilesMobile(props: Props) {
  const {
    dataField,
    onChange,
    errors,
    value,
    type,
    setIdRemoveElem,
    documentCode,
    documentID,
    listUploadFiles,
    openRemoveFileCommentModal,
    setOpenRemoveFileCommentModal,
  } = props;
  const { openPreview, previewLoading, previewFileId, PreviewModal } = useFilePreview(siteUrl,siteUrlProd);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const inputRef = useRef<any>(null);
  const inputId = useId();
  const [files, setFiles] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [loadingFileName, setLoadingFileName] = useState("");
  const [progressUpload, setProgressUpload] = useState(0);
  const objectUrlByFileRef = useRef<Map<File, string>>(new Map());
  const statusUpdateFile = useAppSelector((state: any) => selectUpdateStatus(state, listUploadFiles || documentByCardFilesLibName));
  const statusGetFile = useAppSelector((state: any) => selectGetStatus(state, listUploadFiles || documentByCardFilesLibName));
  const [idxRemoveFile, setIdxRemoveFile] = useState<any>(null);
  const currentUser = useAppSelector(selectCurrentUser);
  const [downloadStateByFile, setDownloadStateByFile] = useState<Record<string, DownloadProgressState>>({});

  const getFileName = (file: any) => file?.name || file?.FileLeafRef || "";
  const getFileKey = (file: any, idx?: number) => String(file?.id || file?.ID || file?.name || file?.FileLeafRef || idx || "");

  const isVideoFile = (file: any) => {
    const name = getFileName(file);
    if (name && VIDEO_EXT_RE.test(name)) return true;
    const mime = file?.type;
    return typeof mime === "string" && mime.startsWith("video/");
  };

  const isDocFile = (file: any) => DOC_EXT_RE.test(getFileName(file));

  const canPreviewFile = (file: any) => {
    const url = file?.url || file?.FileRef;
    if (!url) return false;
    if (!isPreviewable(url)) return false;
    if (isDocFile(file) && type !== 'card') return false;
    return true;
  };

  const getFileLimitBytes = (file: any) => (isVideoFile(file) ? MAX_FILE_SIZE_VIDEO : MAX_FILE_SIZE_DEFAULT);

  const resolveFileUrl = (url: string) => {
    if (!url) return "";
    if (/^(blob:|data:)/i.test(url)) return url;

    const normalizedAbsoluteUrl = url
      .replace(/^https\/\//i, "https://")
      .replace(/^http\/\//i, "http://");

    if (/^https?:\/\/?/i.test(normalizedAbsoluteUrl)) {
      return normalizedAbsoluteUrl.replace(/^https?:\/(?!\/)/i, (match) =>
        match.toLowerCase().startsWith("https") ? "https://" : "http://"
      );
    }

    if (/^\/\//.test(normalizedAbsoluteUrl)) {
      return `${window.location.protocol}${normalizedAbsoluteUrl}`;
    }

    return `${siteUrlOrSiteApi + encodeURI(normalizedAbsoluteUrl)}`;
  };

  const getPlayableUrl = (file: any) => {
    if (file instanceof File) {
      const existing = objectUrlByFileRef.current.get(file);
      if (existing) return existing;
      const created = URL.createObjectURL(file);
      objectUrlByFileRef.current.set(file, created);
      return created;
    }
    const url = file?.url || file?.FileRef;
    if (!url) return "";
    return resolveFileUrl(url);
  };

  const debugLog = (...args: any[]) => {
    if (process.env.NODE_ENV !== "production") {
      console.debug("[UploadFilesMobile]", ...args);
    }
  };

  const errorLog = (message: string, payload?: unknown) => {
    console.error("[UploadFilesMobile]", message, payload);
  };

  useEffect(() => {
    if (value && Array.isArray(value) && value.length > 0) {
      let retFile: any = [];
      let files: any = [];
      files = value || [];
      if (files.length > 0) {
        debugLog("hydrate files", files.flat());
        files.forEach((file: any) => {
          if (file.ID) {
            retFile = [...retFile, {
              url: file.FileRef,
              name: file.FileLeafRef,
              id: file.ID,
            }]
          } else {
            retFile = [...retFile, file]
          }
        });
        setFiles(retFile);
      }
    }
  }, [type, value]);

  useEffect(() => {
    const fileSet = new Set(files.filter((f: any) => f instanceof File));
    const toRevoke: File[] = [];
    objectUrlByFileRef.current.forEach((url, file) => {
      if (!fileSet.has(file)) {
        URL.revokeObjectURL(url);
        toRevoke.push(file);
      }
    });
    toRevoke.forEach((file) => objectUrlByFileRef.current.delete(file));
  }, [files]);

  useEffect(() => {
    return () => {
      objectUrlByFileRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlByFileRef.current.clear();
    };
  }, []);

  const onAllFilesLoaded = () => {
    setLoading(false);
    setLoadingFileName("");
  };

  const handleChange = async (e: any) => {
    e.preventDefault();
    debugLog("handleChange files", e.target.files);
    if (e.target.files && e.target.files.length > 0) {
      let loaded = 0;
      const addedFiles: File[] = [];
      for (let i = 0; i < e.target.files.length; i++) {
        const file = e.target.files[i];
        const limitBytes = getFileLimitBytes(file);
        if (file.size >= limitBytes) {
          alert(
            `${file.name} - этот файл превышает допустимый размер в ${isVideoFile(file) ? "200" : "50"} МB`
          );
        } else {
          const total = e.target.files.length;
          setLoading(true);
          setLoadingFileName(file["name"]);
          const fileReader = new FileReader();
          fileReader.onerror = (err) => {
            errorLog("FileReader error", err);
            setLoading(false);
            setLoadingFileName("");
            alert("Не удалось прочитать файл. Попробуйте выбрать файл снова или используйте другой формат.");
          };
          fileReader.readAsBinaryString(file);
          fileReader.onload = function () {
            loaded++;
            setLoadingFileName("");
            if (loaded == total) {
              onAllFilesLoaded();
            }
          };
          fileReader.onprogress = function (data) {
            if (data.lengthComputable) {
              const progress = parseInt(String((data.loaded / data.total) * 100), 10);
              setProgressUpload(progress);
            }
          };
          // Добавляем логику для загрузки фото с телефона
          const normalizedFile = await normalizeJpeg(e.target.files[i]);
          addedFiles.push(normalizedFile);
        }
      }
      if (addedFiles.length > 0) {
        const updatedFiles = [...files, ...addedFiles];
        debugLog("files state", updatedFiles);
        setFiles(updatedFiles);
        onChange(updatedFiles);
      }
    }
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      let loaded = 0;
      const addedFiles: File[] = [];
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        const file = e.dataTransfer.files[i];
        const limitBytes = getFileLimitBytes(file);
        if (file.size >= limitBytes) {
          alert(
            `${file.name} - этот файл превышает допустимый размер в ${isVideoFile(file) ? "200" : "50"} МB`
          );
        } else {
          setLoading(true);
          setLoadingFileName(file["name"]);
          const total = e.dataTransfer.files.length;
          const fileReader = new FileReader();
          fileReader.onerror = (err) => {
            errorLog("FileReader error (drop)", err);
            setLoading(false);
            setLoadingFileName("");
            alert("Не удалось прочитать файл. Попробуйте выбрать файл снова или используйте другой формат.");
          };
          fileReader.readAsBinaryString(file);
          fileReader.onload = () => {
            loaded++;
            setLoadingFileName("");
            if (loaded == total) {
              onAllFilesLoaded();
            }
          };
          fileReader.onprogress = (data) => {
            if (data.lengthComputable) {
              const progress = parseInt(String((data.loaded / data.total) * 100), 10);
              setProgressUpload(progress);
            }
          };
          addedFiles.push(file);
        }
      }
      if (addedFiles.length > 0) {
        const updatedFiles = [...files, ...addedFiles];
        setFiles(updatedFiles);
        onChange(updatedFiles);
      }
    }
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragEnter = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const removeFile = (e: any, fileName: any, idx: any, idFile: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!idFile) {
      const newArr = [...files];
      newArr.splice(idx, 1);
      setFiles(newArr);
      onChange(newArr);
    } else {
      if (type === 'add_comment') {
        localStorage.setItem('removeCommentFileInActionCard', JSON.stringify(idFile));
        setOpenRemoveFileCommentModal(!openRemoveFileCommentModal);
      } else if (type === 'audit_card_my_tasks') {
        localStorage.setItem('removeAuditFile', JSON.stringify(idFile));
        setOpenRemoveFileCommentModal(!openRemoveFileCommentModal);
      } else {
        setIdRemoveElem({ listName: listUploadFiles || documentByCardFilesLibName, ID: idFile });
      }
      setIdxRemoveFile(idx);
    }
  };

  useEffect(() => {
    if (statusUpdateFile === "SUCCESS" && idxRemoveFile !== null) {
      const newArr = [...files];
      newArr.splice(idxRemoveFile, 1);
      setFiles(newArr);
      onChange(newArr);
      setIdxRemoveFile(null);
    }
  }, [statusUpdateFile]);

  const resetAndOpenFilePicker = () => {
    try {
      if (!inputRef.current) {
        errorLog("file input ref is null");
        alert("Не удалось открыть окно выбора файла. Перезагрузите страницу и попробуйте снова.");
        return;
      }
      inputRef.current.value = "";
      inputRef.current.click();
    } catch (err) {
      errorLog("failed to open file picker", err);
      alert("Не удалось открыть окно выбора файла. Проверьте настройки браузера и попробуйте снова.");
    }
  };

  const updateDownloadState = (fileKey: string, patch: Partial<DownloadProgressState>) => {
    setDownloadStateByFile((prev) => ({
      ...prev,
      [fileKey]: {
        progress: prev[fileKey]?.progress || 0,
        loadedBytes: prev[fileKey]?.loadedBytes || 0,
        totalBytes: prev[fileKey]?.totalBytes ?? null,
        isLoading: prev[fileKey]?.isLoading || false,
        ...patch,
      },
    }));
  };

  const clearDownloadState = (fileKey: string, delayMs = 0) => {
    window.setTimeout(() => {
      setDownloadStateByFile((prev) => {
        const next = { ...prev };
        delete next[fileKey];
        return next;
      });
    }, delayMs);
  };

  const startBrowserDownload = (blob: Blob, name: string) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const linkDownLoad: HTMLAnchorElement = document.createElement("a");
    linkDownLoad.href = blobUrl;
    linkDownLoad.download = name;
    document.body.appendChild(linkDownLoad);
    linkDownLoad.click();
    linkDownLoad.remove();
    window.setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 1000);
  };

  const startDirectDownload = (resolvedUrl: string, name: string) => {
    const linkDownLoad: HTMLAnchorElement = document.createElement("a");
    linkDownLoad.href = resolvedUrl;
    linkDownLoad.download = name;
    document.body.appendChild(linkDownLoad);
    linkDownLoad.click();
    linkDownLoad.remove();
  };

  const downloadFileWithProgress = (resolvedUrl: string, name: string, fileKey: string) =>
    new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", resolvedUrl, true);
      xhr.responseType = "blob";

      xhr.onprogress = (event) => {
        const totalBytes = event.lengthComputable ? event.total : null;
        const progress =
          totalBytes && totalBytes > 0
            ? Math.min(100, Math.round((event.loaded * 100) / totalBytes))
            : 0;

        updateDownloadState(fileKey, {
          progress,
          loadedBytes: event.loaded,
          totalBytes,
          isLoading: true,
        });
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          updateDownloadState(fileKey, {
            progress: 100,
            loadedBytes: xhr.response?.size || downloadStateByFile[fileKey]?.loadedBytes || 0,
            totalBytes: xhr.response?.size || downloadStateByFile[fileKey]?.totalBytes || null,
            isLoading: false,
          });
          startBrowserDownload(xhr.response, name);
          clearDownloadState(fileKey, 1200);
          resolve();
          return;
        }

        reject(new Error(`Download failed with status ${xhr.status}`));
      };

      xhr.onerror = () => reject(new Error("Download failed"));
      xhr.onabort = () => reject(new Error("Download aborted"));
      xhr.send();
    });

  // const downloadForApp = async (file: FileType) => {
  //   const { value: token } = await SecureStoragePlugin.get({
  //     key: "accessToken",
  //   });

  //   const response = await CapacitorHttp.request({
  //     method: "GET",
  //     url: file.FileRef,
  //     headers: {
  //       Authorization: `Bearer ${token}`,
  //     },
  //     responseType: "arraybuffer",
  //   });

  //   if (response.status !== 200) {
  //     throw new Error(`HTTP ${response.status}`);
  //   }

  //   const saved = await Filesystem.writeFile({
  //     path: file.FileLeafRef,
  //     data: response.data,
  //     directory: Directory.External,
  //     recursive: true,
  //   });

  //   await FileOpener.open({
  //     filePath: saved.uri,
  //   });
  // };

 const downLoadHandle = async (
    e: any,
    url: string,
    name: string,
    idFile: number,
    fileKey: string
  ) => {
    e.preventDefault();

    if (downloadStateByFile[fileKey]?.isLoading) {
      return;
    }

    if (type === "card") {
      addAnalysis(
        currentUser,
        documentID,
        documentCode,
        "isActive",
        "Download"
      );
    }

    // APK: скачивание через Capacitor
    if (!isPWA()) {
      updateDownloadState(fileKey, {
        progress: 0,
        loadedBytes: 0,
        totalBytes: null,
        isLoading: true,
      });

      try {
        const file: FileType = {
          FileRef: resolveFileUrl(url),
          FileLeafRef: name,
        } as FileType;

        await downloadForApp(file);
      } catch (error) {
        errorLog("APK download failed", error);
      } finally {
        updateDownloadState(fileKey, {
          progress: 100,
          isLoading: false,
        });

        clearDownloadState(fileKey, 800);
      }

      return;
    }

    // PWA: остаётся существующая логика скачивания
    const resolvedUrl = resolveFileUrl(url);

    updateDownloadState(fileKey, {
      progress: 0,
      loadedBytes: 0,
      totalBytes: null,
      isLoading: true,
    });

    if (/^(blob:|data:)/i.test(resolvedUrl)) {
      startDirectDownload(resolvedUrl, name);

      updateDownloadState(fileKey, {
        progress: 100,
        loadedBytes: 0,
        totalBytes: null,
        isLoading: false,
      });

      clearDownloadState(fileKey, 800);
      return;
    }

    try {
      await downloadFileWithProgress(resolvedUrl, name, fileKey);
    } catch (error) {
      errorLog("download failed", error);
      clearDownloadState(fileKey);
      window.location.assign(resolvedUrl);
    }
  };


  return (
    <div className={`${dataField.width} mb-5`}>
      {files.filter(isVideoFile).length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-medium leading-6 text-gray-900 mb-2.5">Видео · {files.filter(isVideoFile).length}</h3>
          <div className="flex flex-col gap-5">
            {files
              .filter(isVideoFile)
              .map((file: any, idx: number) => (
                <div key={file.id || file.name || idx} className="w-full" onClick={() => console.log('VIDEO URL:', getPlayableUrl(file))}>
                  <div className="text-[14px] mb-2 break-words" >{getFileName(file)}</div>
                  {isPWA() ? (
                    <video
                      className="w-full rounded-lg bg-black"
                      controls
                      playsInline
                      preload="metadata"
                      src={getPlayableUrl(file)}
                    />
                  ) : (
                    <MobileVideo file={file} />
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
      {(files.length === 0 || (dataField.multiple && !dataField.disabled)) && (
        <div
          onDragEnter={handleDragEnter}
          onDrop={handleDrop}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          className="relative flex justify-center px-6 py-10 mt-2 border border-dashed rounded-lg border-gray-900/25"
        >
          {!loading && (
            <div className="text-center">
              {(statusGetFile === "LOADING" && <Loading />) || (
                <>
                  <DocumentIcon className="w-12 h-12 mx-auto text-gray-300" aria-hidden="true" />
                  <div className="my-3.5 flex flex-col-reverse text-sm leading-6 text-gray-600">
                    <p className="hidden sm:block pl-1 text-base">{dataField.title}</p>
                    <div className="text-base relative inline-flex outline-none rounded-md bg-white font-semibold text-[#012169]">
                      <label
                        htmlFor={inputId}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            resetAndOpenFilePicker();
                          }
                        }}
                        className={`${dataField.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"} relative z-10`}
                        tabIndex={dataField.disabled ? -1 : 0}
                      >
                        {dataField.titleFile}
                      </label>
                      <input
                        ref={inputRef}
                        multiple={dataField.multiple || false}
                        onChange={handleChange}
                        onClick={(e) => {
                          try {
                            (e.currentTarget as HTMLInputElement).value = "";
                          } catch (err) {
                            errorLog("failed to reset input value", err);
                          }
                        }}
                        accept={(type === 'card' || type === 'add_card') && ".xlsx, .docx, .pdf, .pptx, .txt, .mp4, .avi, .mpeg" || undefined}
                        id={inputId}
                        name={inputId}
                        type="file"
                        disabled={!!dataField.disabled}
                        className={`${dataField.disabled ? "pointer-events-none" : "cursor-pointer"} absolute inset-0 z-20 opacity-0`}
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">{dataField.placeholder}</p>
                </>
              )}
            </div>
          )}
          {dataField.require && (
            <div className="text-[#BA0C2F] absolute z-20 right-0 top-[-45px] flex flex-row justify-between">
              {Object.keys(errors).length > 0 && errors[dataField.key] && (
                <span className="text-[#BA0C2F]">Заполните поле&nbsp;</span>
              )}
              <span className="text-[#BA0C2F]">*</span>
            </div>
          )}
          {loading && (
            <div className="flex flex-col items-center justify-center w-full">
              <div className="mb-4 h-6 max-w-md w-full bg-[#DFE4EA] rounded-full">
                <div className="h-6 bg-[#012169] rounded-full" style={{ width: `${progressUpload}%` }} />
              </div>
              <div className="text-lg mb-4 text-[#637381] font-medium">Загрузка файла...</div>
              <div
                className="text-sm text-[#637381] max-w-full truncate"
                title={loadingFileName || ""}
              >
                {formatFileNameForUi(loadingFileName)}
              </div>
            </div>
          )}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-col items-center justify-center w-full mt-5">
          {files.map((file: any, idx: number) => (
            (() => {
              const fileKey = getFileKey(file, idx);
              const downloadState = downloadStateByFile[fileKey];
              const isDownloading = !!downloadState?.isLoading;
              const progressValue = downloadState?.progress || 0;
              const loadedText = downloadState ? formatBytes(downloadState.loadedBytes) : "";
              const totalText = downloadState?.totalBytes ? formatBytes(downloadState.totalBytes) : "";
              const progressText = downloadState?.totalBytes
                ? `${progressValue}% · ${loadedText} / ${totalText}`
                : loadedText
                  ? `${loadedText} загружено`
                  : "Подготовка скачивания...";
              const previewable = canPreviewFile(file);

              return (
            <a
              onClick={(e) => {
                const url = file.url || URL.createObjectURL(file);
                if (previewable) {
                  e.preventDefault();
                  e.stopPropagation();
                  openPreview(url, file.id);
                } else {
                  e.preventDefault();
                  e.stopPropagation();
                  downLoadHandle(e, url, file.name, file.id, fileKey);
                }
              }}
              href="#"
              key={file.id || file.name || idx}
              className="box-border relative z-10 flex flex-row justify-between w-full gap-3 px-4 py-2 mb-5 rounded-lg cursor-pointer upload bg-sp-gray"
            >
              <div className="flex flex-col grow min-w-0">
                <div className="flex flex-row gap-x-2.5 min-w-0">
                  <div className="flex flex-col items-center justify-center">
                    <FileIcon />
                  </div>
                  <div className="flex flex-col min-w-0 grow">
                    <div
                      className="text-[14px] tablet:text-[16px] min-w-0 max-w-full truncate"
                      title={file?.name || ""}
                    >
                      {formatFileNameForUi(file?.name)}
                    </div>
                    {downloadState && (
                      <div className="mt-1 min-w-0">
                        <div className="text-[11px] leading-4 text-[#637381] truncate">{progressText}</div>
                        <div className="mt-1 h-1 w-full bg-[#DFE4EA] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#012169] rounded-full transition-all"
                            style={{ width: `${Math.max(progressValue, 8)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="relative z-10 flex flex-row">
                {previewable && (
                  <span className="inline-block mr-[10px] mt-[3px]">
                    {previewLoading && previewFileId === file.id ? (
                      <FlexCol className="flex w-6 h-6">
                        <Loading />
                      </FlexCol>
                    ) : (
                      <EyeIcon />
                    )}
                  </span>
                )}
                <span onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  downLoadHandle(
                    e,
                    file.url || URL.createObjectURL(file),
                    file.name,
                    file.id,
                    fileKey
                  );
                }} className="inline-block mr-[30px]">
                  {isDownloading ? (
                    <FlexCol className="flex w-6 h-6">
                      <Loading />
                    </FlexCol>
                  ) : (
                    <DownloadIcon />
                  )}
                </span>
                {!dataField.disabled && (
                  <>
                    {(statusUpdateFile === "LOADING" && <Loading />) || (
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeFile(e, file.name, idx, file.id)
                        }}
                        className="absolute right-0 z-20 hidden tablet:inline-block"
                      >
                        <Trash />
                      </span>
                    )}
                  </>
                )}
              </div>
            </a>
              );
            })()
          ))}
        </div>
      )}

      {PreviewModal}
    </div>
  );
}

export default UploadFilesMobile;
